import { Loader2, BarChart3, Users, Fish, Sparkles, Layers } from 'lucide-react';
import { useSpeciesStats } from '../hooks/useSpeciesAdmin';
import { SpeciesRelatedPanel } from './SpeciesRelatedPanel';
import { SpeciesCromosAlbumPanel } from './SpeciesCromosAlbumPanel';

interface SpeciesAppDataPanelProps {
    speciesCatalogId: number;
    scientificName: string;
    waterType: 'salada' | 'dulce' | 'both';
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-border bg-card/50 p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                {icon}
                <span className="text-xs font-medium">{label}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{value}</p>
        </div>
    );
}

export function SpeciesAppDataPanel({ speciesCatalogId, scientificName, waterType }: SpeciesAppDataPanelProps) {
    const { data: stats, isLoading } = useSpeciesStats(speciesCatalogId);

    return (
        <div className="space-y-8">
            <section className="space-y-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">
                    Uso en la app
                </h3>
                {isLoading ? (
                    <Loader2 size={20} className="animate-spin text-muted-foreground" />
                ) : stats ? (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <StatCard label="Capturas" value={stats.capture_count} icon={<Fish size={14} />} />
                            <StatCard label="Pescadores" value={stats.unique_anglers} icon={<Users size={14} />} />
                            <StatCard label="Cromos desbloqueados" value={stats.cromo_unlock_count} icon={<Sparkles size={14} />} />
                            <StatCard label="Relaciones" value={stats.related_count} icon={<Layers size={14} />} />
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                            <span className={`px-2 py-1 rounded ${stats.has_details ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
                                {stats.has_details ? 'Ficha completa' : 'Sin ficha / incompleta'}
                            </span>
                            <span className={`px-2 py-1 rounded ${stats.in_cromos_album ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                {stats.in_cromos_album ? 'En álbum Cromos' : 'Fuera del álbum Cromos'}
                            </span>
                        </div>
                    </>
                ) : (
                    <p className="text-sm text-muted-foreground">No se pudieron cargar estadísticas.</p>
                )}
            </section>

            <section className="rounded-xl border border-border bg-card/50 p-6 shadow-sm">
                <SpeciesCromosAlbumPanel
                    scientificName={scientificName}
                    inCromosAlbum={stats?.in_cromos_album ?? false}
                    cromosDisplayName={stats?.cromos_album_display_name}
                    cromosRarity={stats?.cromos_album_rarity}
                    defaultWaterType={waterType}
                />
            </section>

            <section className="rounded-xl border border-border bg-card/50 p-6 shadow-sm">
                <SpeciesRelatedPanel speciesCatalogId={speciesCatalogId} />
            </section>

            <p className="text-xs text-muted-foreground flex items-start gap-2">
                <BarChart3 size={14} className="shrink-0 mt-0.5" />
                <span>
                    Las capturas se resuelven por nombre científico o nombres comunes en texto libre.
                    Para calidad de datos masiva usa scripts en <code>scripts/species-details/</code>.
                </span>
            </p>
        </div>
    );
}
